import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  processNodeAnalyticsData,
  processSimulationsAnalyticsData,
  processTestRunAnalyticsData,
} from "src/analytics/processAnalyticsFile";
import { parseJsonStream } from "src/analytics/streamJson";
import { AnalyticsKey, VERSION_KEY } from "src/analytics/utils";
import {
  MultiversionTestRunResults,
  PresignedUrlRequest,
  RouterMultiversionTestRun,
  RouterPresignedUrls,
  SimulationAnalyticsPresignedUrlRequest,
} from "src/api/endpoints";
import { GenericObject } from "src/api/flowTypes";
import { NodeTypes } from "src/clients/flow-api";
import {
  useAuthoringContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import { useSimulationRun } from "src/simulations/useSimulationRun";
import { useNodeRunState } from "src/store/runState/RunState";
import { logger } from "src/utils/logger";

export type DataPoint = GenericObject & {
  [VERSION_KEY]: string;
};
export type ComparativeAnalyticsFile = {
  data: DataPoint[];
  combined_successful_count: number;
  successful_count_by_version: Record<string, number>;
};

export type ComparativeAnalyticsSummary = Record<
  string,
  { success_count: number; ignored_count: number; failed_count: number }
>;

export type ComparativeAnalyticsData =
  | {
      type: "error";
      reason: "SOME_VERSION_ERRORED";
      erroredVersionNames: string[];
      isMultiversion: boolean;
    }
  | {
      type: "error";
      reason: "NO_SUCCESSFUL_ROWS";
      isMultiversion: boolean;
    }
  | ({
      type: "success";
      keys: AnalyticsKey[];
      total_count: number;
      summary: ComparativeAnalyticsSummary;
      isMultiversion: boolean;
    } & ComparativeAnalyticsFile);

export type OutcomeDataFile = {
  // The string is the row id
  data: Record<string, GenericObject>;
};

const useMultiversionTestRun = () => {
  const { workspace } = useAuthoringContext();
  const baseUrl = workspace.base_url!;

  const runState = useNodeRunState("output");
  const testRunId =
    runState?.type === "test-run" ? runState.testResult.test_run_id : null;

  const allVersionsAreFinished = (response: MultiversionTestRunResults) => {
    return response.flow_versions.every(
      (version) => !!version.execution_results,
    );
  };

  return useQuery<MultiversionTestRunResults>({
    queryKey: ["multiversionTestRun", baseUrl, testRunId],
    queryFn: async () => {
      let testRunResult = await RouterMultiversionTestRun.get(
        baseUrl,
        testRunId!,
      );

      while (
        testRunResult.status === 200 &&
        !allVersionsAreFinished(testRunResult.data)
      ) {
        await new Promise((r) => setTimeout(r, 1000));
        testRunResult = await RouterMultiversionTestRun.get(
          baseUrl,
          testRunId!,
        );
      }

      if (testRunResult.status !== 200) {
        throw new Error(
          `Got an error ${testRunResult.status} while polling the test run endpoint`,
        );
      }

      return testRunResult.data;
    },
    enabled: Boolean(testRunId && baseUrl),
  });
};

const useGetPresignedFile = <T>(
  testRunId?: string,
  presignedUrlParams?: PresignedUrlRequest,
  options: {
    enabled?: boolean;
    initialData?: T;
  } = { enabled: true },
) => {
  const { workspace } = useWorkspaceContext();
  const baseUrl = workspace.base_url!;

  return useQuery({
    queryKey: [
      "presignedFile",
      baseUrl,
      testRunId,
      JSON.stringify(presignedUrlParams),
    ],
    queryFn: async () => {
      if (!presignedUrlParams) {
        logger.error(
          `useGetPresignedFile: Presigned url params are not defined ${testRunId}`,
        );
        return;
      }

      const presignedUrlResponse = await RouterPresignedUrls.post(
        baseUrl,
        testRunId!,
        presignedUrlParams,
      );

      if (presignedUrlResponse.status !== 200) {
        throw new Error(
          `Got an error ${presignedUrlResponse.status} while getting a presigned url for testRunId: ${testRunId}, workspaceId: ${workspace.id}, params: ${JSON.stringify(presignedUrlParams)}`,
        );
      }

      return await fetchWithPolling<T>(
        presignedUrlResponse.data.url,
        presignedUrlParams,
      );
    },
    enabled: Boolean(
      options.enabled && testRunId && baseUrl && presignedUrlParams,
    ),
    // Do not retry, as we poll inside the query
    retry: false,
    refetchOnMount: false,
    initialData: options.initialData,
  });
};

export const useSimulationsAnalyticsData = (
  options: { enabled?: boolean } = { enabled: true },
) => {
  const isMultiversion = false;
  const { workspace, flow, version, simulationId = "" } = useAuthoringContext();
  const { data: simulationRun } = useSimulationRun(
    flow.id,
    version.id,
    simulationId,
  );

  const analyticsFileOptions: SimulationAnalyticsPresignedUrlRequest = {
    type: "simulation_analytics",
    job_run_id: simulationId,
  };

  const { data: combinedAnalyticsFile } =
    useGetPresignedFile<ComparativeAnalyticsFile>(
      simulationId,
      analyticsFileOptions,
    );

  const baseUrl = workspace.base_url!;

  return useQuery<ComparativeAnalyticsData>({
    queryKey: [
      "comparativeAnalyticsSimulation",
      baseUrl,
      simulationId,
      simulationRun?.total_rows_count,
      simulationRun?.ignored_rows_count,
    ],
    queryFn: async () => {
      if (!simulationId || !combinedAnalyticsFile || !simulationRun) {
        const fileError = !combinedAnalyticsFile
          ? "Combined analytics file is not defined"
          : "Simulation run is not defined";

        throw new Error(
          !simulationId ? "Simulation is not defined" : fileError,
        );
      }
      return processSimulationsAnalyticsData({
        combinedAnalyticsFile,
        isMultiversion,
        totalRowsCount: simulationRun.total_rows_count,
        ignoredRowsCount: simulationRun.ignored_rows_count,
      });
    },
    enabled: Boolean(
      options.enabled &&
        baseUrl &&
        simulationId &&
        combinedAnalyticsFile &&
        simulationRun,
    ),
  });
};

export const useComparativeAnalyticsData = (
  options: { enabled?: boolean } = { enabled: true },
) => {
  const { data: testRunResult } = useMultiversionTestRun();
  const testRunId = testRunResult?.id;

  const { data: combinedAnalyticsFile } =
    useGetPresignedFile<ComparativeAnalyticsFile>(testRunId, {
      type: "analytics",
    });
  const { data: outcomesFile } = useGetPresignedFile<OutcomeDataFile>(
    testRunId,
    testRunResult
      ? {
          type: "outcome_data",
          dataset_id: testRunResult.dataset.id,
          dataset_etag: testRunResult.dataset.etag,
        }
      : undefined,
    {
      enabled: options.enabled,
    },
  );

  const { flow, workspace } = useAuthoringContext();
  const baseUrl = workspace.base_url!;

  return useQuery<ComparativeAnalyticsData>({
    queryKey: ["comparativeAnalytics", baseUrl, testRunId],
    queryFn: async () => {
      if (!testRunResult || !combinedAnalyticsFile || !outcomesFile) {
        const fileError = !combinedAnalyticsFile
          ? "Combined analytics file is not defined"
          : "Outcome file is not defined";

        throw new Error(
          !testRunResult ? "Test run result is not defined" : fileError,
        );
      }
      return processTestRunAnalyticsData(
        combinedAnalyticsFile,
        outcomesFile,
        testRunResult,
        flow,
      );
    },
    enabled: Boolean(
      options.enabled &&
        baseUrl &&
        testRunResult &&
        combinedAnalyticsFile &&
        outcomesFile,
    ),
  });
};

export const useTestRunOrSimulationAnalyticsData = (
  options: { enabled?: boolean } = { enabled: true },
) => {
  const { simulationId } = useAuthoringContext();

  const testRunAnalytics = useComparativeAnalyticsData({
    enabled: !simulationId && options.enabled,
  });
  const simulationAnalytics = useSimulationsAnalyticsData({
    enabled: !!simulationId && options.enabled,
  });

  return simulationId ? simulationAnalytics : testRunAnalytics;
};

export type NodeAnalyticsRow = {
  row_id: string;
  meta: {
    case_id: string;
    case_idx: number;
    case_name: string;
    index: number;
    node_name: string;
    node_type: NodeTypes.SPLIT_2 | NodeTypes.RULE_2 | NodeTypes.DECISION_TABLE;
    start_time?: string;
    entity_id?: string;
    decision_id?: string;
  };
  node_data: GenericObject;
  output_data: GenericObject;
  aux_data: GenericObject;
};

export type NodeAnalyticsRowWithOutcomes = NodeAnalyticsRow & {
  outcome_data: GenericObject;
};

export type NodeAnalyticsFile = {
  data: NodeAnalyticsRow[];
  successful_count: number;
};

export type NodeAnalyticsData =
  | {
      type: "error";
      reason: "SOME_VERSION_ERRORED";
      erroredVersionNames: string[];
      isMultiversion: false;
    }
  | {
      type: "error";
      reason: "NO_SUCCESSFUL_ROWS";
      isMultiversion: false;
    }
  | ({
      type: "success";
      keys: AnalyticsKey[];
      isMultiversion: false;
      // the rows the charts read have the outcomes file joined onto them
      data: NodeAnalyticsRowWithOutcomes[];
    } & Omit<NodeAnalyticsFile, "data">);

export const useNodeAnalyticsData = (
  nodeId: string,
  options: { enabled?: boolean } = { enabled: true },
) => {
  const { data: testRunResult } = useMultiversionTestRun();
  const testRunId = testRunResult?.id;
  const runState = useNodeRunState("output");

  const shortTestResult =
    runState?.type === "test-run" ? runState.testResult : null;
  const versionId = shortTestResult?.version_id;

  const { data: nodeAnalyticsFile } = useGetPresignedFile<NodeAnalyticsFile>(
    testRunId,
    testRunId && versionId && nodeId
      ? {
          type: "node_analytics",
          run_id: testRunId,
          version_id: versionId,
          node_id: nodeId,
        }
      : undefined,
  );

  const { data: outcomesFile } = useGetPresignedFile<OutcomeDataFile>(
    testRunId,
    testRunResult
      ? {
          type: "outcome_data",
          dataset_id: testRunResult.dataset.id,
          dataset_etag: testRunResult.dataset.etag,
        }
      : undefined,
    {
      enabled: options.enabled,
    },
  );

  const { flow, workspace } = useAuthoringContext();
  const baseUrl = workspace.base_url!;

  const executionResults = testRunResult?.flow_versions.find(
    (v) => v.id === versionId,
  )?.execution_results;

  const nodeWasExecuted =
    executionResults?.type === "success" && executionResults?.nodes[nodeId];

  return useQuery<NodeAnalyticsData>({
    queryKey: ["nodeAnalytics", baseUrl, testRunId, nodeId],
    queryFn: async (): Promise<NodeAnalyticsData> => {
      if (!nodeAnalyticsFile || !outcomesFile) {
        throw new Error(
          !nodeAnalyticsFile
            ? "Node analytics file is not defined"
            : "Outcome file is not defined",
        );
      }

      return processNodeAnalyticsData(
        executionResults,
        nodeAnalyticsFile,
        outcomesFile,
        testRunResult,
        flow,
        nodeId,
      );
    },
    enabled: Boolean(
      options.enabled &&
        baseUrl &&
        nodeWasExecuted &&
        nodeAnalyticsFile &&
        nodeId,
    ),
    refetchInterval: Infinity,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });
};

const fetchWithPolling = async <T>(
  url: string,
  params: GenericObject,
  timeout = 5 * 60 * 1000,
): Promise<T> => {
  const startTime = Date.now();

  while (true) {
    const response = await fetch(url);

    if (response.status === 200) {
      return parseJsonStream<T>(response);
    }

    if (Date.now() - startTime > timeout) {
      throw new Error(
        `Timeout after polling the file for ${timeout / 1000} seconds: ${JSON.stringify(params)}`,
      );
    }

    if (response.status !== 404) {
      throw new Error(
        `Got an error ${response.status} while getting the file: ${JSON.stringify(params)}`,
      );
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
};
